import { h as __h } from "preact";
import { Component, createRef } from "preact";
/** @import { ComponentChildren, RefObject } from "preact"; */

import Float from "~/util/float.js";
/** @import { Placement } from "~/util/float.js"; */
import html from "~/util/html.js";
import { adopt, importCss, CSS_MODULE_SCRIPTS } from "~/util/css.js";
const {
  default: sheet
} = CSS_MODULE_SCRIPTS ? await import("./Popover.module.css", {
  with: {
    type: "css"
  }
}) : await importCss("./Popover.module.css", import.meta.url);
const css = adopt(sheet);

/**
 * @typedef {object} Props
 * @property {string} id
 * @property {ComponentChildren | undefined} [trigger] - This _must_ be the root element passed to
 *   the `trigger` prop.
 * @property {ComponentChildren | undefined} [children]
 * @property {HTMLElement | undefined} [anchor] - Optional fallback anchor when
 *   previousElementSibling isn't accurate
 * @property {"auto" | "hint" | "manual"} [popover]
 * @property {Placement} placement
 * @property {((ev: PointerEvent & { currentTarget: HTMLDivElement }) => void) | undefined} [onClick]
 * @property {(() => void) | undefined} [onClose]
 * @property {RefObject<Popover>} [ref]
 */

/** @extends {Component<Props>} */
export default class Popover extends Component {
  /** @type {RefObject<HTMLDivElement>} */
  #ref = createRef();
  #float = new Float({
    placement: this.props.placement
  });

  /** @param {HTMLElement} el */
  setReference = el => {
    return this.#float.setReferenceRef(el);
  };
  show() {
    this.#float.show();
    this.#ref.current?.showPopover({
      source: this.props.anchor
    });
  }
  hide() {
    this.#ref.current?.hidePopover();
    this.#float.hide();
  }

  /** @override */
  componentWillUnmount() {
    if (this.#ref.current?.matches(":popover-open")) this.#ref.current.hidePopover();
    this.#float.hide();
  }

  /** @param {ToggleEvent & { currentTarget: HTMLDivElement }} ev */
  #onToggle = ev => {
    const popover = ev.currentTarget;

    // the type assertion on ev.source and (fallback to ev.currentTarget.previousElementSibling)
    // are there because ToggleEvent.source only has ~57% support per caniuse
    // https://caniuse.com/mdn-api_toggleevent_source
    //
    // this fallback is the reason that the triggering element must be the root element
    // passed to the `trigger` prop
    const source = ev.source ?? this.props.anchor ?? popover.previousElementSibling;
    if (ev.newState === "closed") {
      this.#float.hide();
      // reset all forms within the popover
      for (const form of popover.querySelectorAll("form")) {
        form.reset();
      }
      this.props.onClose?.();
    } else if (source instanceof HTMLElement) {
      this.#float.show(source);

      /** @type {HTMLElement | null} */
      const focusable = popover.querySelector("[autofocus]") ?? popover.querySelector("a, button, input, textarea");
      focusable?.focus();
    }
  };

  /** @param {FocusEvent & { currentTarget: HTMLDivElement }} ev */
  #onFocusOut = ev => {
    const menu = ev.currentTarget;
    const focused = ev.relatedTarget;
    if (!(focused instanceof Node)) return;
    if (menu.contains(focused)) return;
    if (focused instanceof HTMLButtonElement && focused.popoverTargetElement === menu) return;
    menu.hidePopover();
  };
  render() {
    return [this.props.trigger, __h("div", {
      "class": css.popover,
      ref: (/** @type {HTMLDivElement} */ref) => {
        this.#ref.current = ref;
        this.#float.setFloatingRef(ref);
      },
      id: this.props.id,
      popover: this.props.popover ?? "auto",
      onToggle: this.#onToggle,
      onFocusOut: this.#onFocusOut,
      onClick: this.props.onClick,
      "data-placement": this.#float.placement,
      style: this.#float.styles.float
    }, this.props.children)];
  }
}